import { bindable } from 'aurelia-templating';
import { autoinject, transient } from 'aurelia-dependency-injection';
import { computedFrom } from "aurelia-binding";
import { Reflection } from '../../../utilities/reflection';

@autoinject
@transient()
export class Collapsable {

    @bindable
    public label: string | (() => string) | ({ html: string, bindingContext: any });

    @bindable
    public isOpen: boolean = true;

    @bindable
    public isTitleHtml: boolean = false;

    //If the expander should have animation or not (due to performance issues when multiple expanders expand/collapse simultaneously)
    @bindable
    public hasAnimation: boolean = true;

    //Is the content section being currently resized
    public isSectionResizing: boolean;

    //Hold value of the content section's max-height value
    private sectionContentMaxHeight: number = 0;

    //The count (pixels) by which the content will collapse/expand for each interval
    private expansionIncrementCount: number = 4;

    //The pace (interval timeout) by which the content will collapse/expand
    private expansionIncrementPace: number = 0.75; //Milliseconds

    /* The increment that is added to the 'expansionIncrementCountMultiplier' within the expand/collapse methods
       This 'expansionIncrementCountMultiplier' multiplied by the 'expansionIncrementCount' allows the content section to
       collapse/expand in a non-linear/exponential way.
       Increasing this value will speed up the collapse/expansion transitions exponentially, and vice-versa */
    private expansionMultiplierIncrement = 0.025;

    //Holds reference to interval id (setInterval) that expands/shrinks the collapsable
    private expansionIntervalId;

    //Reference to the content section within the collapsable
    private collapsableContentRef: HTMLElement;

    attached() {
        //Set initial 'collapsed' state
        if (!this.isOpen) {
            this.setCollapsableContentMaxHeight(0);
        }
    }

    detached() {
        this.clearExpansionInterval();
    }

    /**
     * isOpen prop changed
     */
    isOpenChanged() {

        //Cancel any existing resizing
        if (this.isSectionResizing) {
            this.stopContentResizing();
            this.isSectionResizing = false;
        }
        this.isSectionResizing = true;

        if (this.collapsableContentRef) {
            if (!this.isOpen) {
                this.collapseSectionContentIncrementally();

            } else {
                this.expandSectionContentIncrementally();
            }
        }
    }

    @computedFrom(Reflection.getPropertyPath<Collapsable>(x => x.label))
    get titleInternal(): any {
        if (typeof (this.label) === "function") {
            return this.label();
        }
        return this.label;
    }

    //Prevents from closing/opening the collapsable when its HTML Title
    preventClickPropagation() {
        event.stopPropagation();
    }

    public toggle() {
        this.isOpen = !this.isOpen;
    }

    /**
     * Set the max-height of the collapsable's content section
     * @param maxHeight Max height
     */
    setCollapsableContentMaxHeight = (maxHeight: number) => {
        this.sectionContentMaxHeight = maxHeight;
        if (this.collapsableContentRef) {
            this.collapsableContentRef.style.maxHeight = `${this.sectionContentMaxHeight}px`;
        }
    }

    /**
     * Increase the content section's max-height by a value
     * @param increaseByValue Increase by value
     */
    increaseCollapsableContentMaxHeight = (increaseByValue: number) => { this.setCollapsableContentMaxHeight(this.sectionContentMaxHeight + increaseByValue); }

    /**
     * Decrease the content section's max-height by a value
     * @param increaseByValue Decrease by value
     */
    decreaseCollapsableContentMaxHeight = (decreaseByValue: number) => { this.setCollapsableContentMaxHeight(this.sectionContentMaxHeight - decreaseByValue); }

    /**
     * Expand the content transitionally (the height of the collapsable content increases exponentially until it fits all the content)
     */
    expandSectionContentIncrementally = () => {

        if (this.hasAnimation) {

            //The 'latestSectionContentHeight' is used to keep track of the latest height of the content section
            let latestSectionContentHeight = this.getSectionContentHeight();
            let expansionIncrementCountMultiplier = 1;

            //Set the component's content section's maximum height to its current height
            this.setCollapsableContentMaxHeight(this.getSectionContentHeight());
            //Start increasing the content section's height
            this.increaseCollapsableContentMaxHeight(this.expansionIncrementCount);

            this.clearExpansionInterval();

            this.expansionIntervalId = setInterval(() => {
                //If the content's height remained the same after increasing the height in the previous interval, stop increasing height
                if (this.getSectionContentHeight() === latestSectionContentHeight) {
                    this.setCollapsableContentMaxHeight(latestSectionContentHeight);
                    this.stopContentResizing();
                    //Once the transition is complete, the content height needs to be able to stretch dynamically i.e. when the window is narrowed/widened
                    this.collapsableContentRef.style.maxHeight = 'none';
                    return;
                }

                //Update the 'latestSectionContentHeight' variable before increasing the allowed max-height
                latestSectionContentHeight = this.getSectionContentHeight();

                //Increment the content section's allowed max-height
                this.increaseCollapsableContentMaxHeight(this.expansionIncrementCount * expansionIncrementCountMultiplier);

                //Increase the height increment multiplier
                expansionIncrementCountMultiplier += this.expansionMultiplierIncrement;
            }, this.expansionIncrementPace);
        }
        else {
            this.isSectionResizing = false;
            this.collapsableContentRef.style.maxHeight = 'none';
        }
    }

    /**
     * Collapse the content transitionally (the height of the collapsable content decreases exponentially until it reaches 0)
     */
    collapseSectionContentIncrementally = () => {

        if (this.hasAnimation) {

            let expansionIncrementCountMultiplier = 1;

            //Set the component's content section's maximum height to its current height
            this.setCollapsableContentMaxHeight(this.getSectionContentHeight());
            //Decrease the maximum height of the content section. Decrease either by an increment value, or by the remaining content section's height
            const decreaseByValue = this.expansionIncrementCount < this.sectionContentMaxHeight ? this.expansionIncrementCount : this.sectionContentMaxHeight;
            //Math.max is used to eliminate any negative values
            this.decreaseCollapsableContentMaxHeight(Math.max(0, decreaseByValue));

            this.clearExpansionInterval();

            this.expansionIntervalId = setInterval(() => {
                //If the content section's height reached 0 (the content section is collapsed)
                if (this.getSectionContentHeight() === 0) {
                    this.stopContentResizing();
                    return;
                }

                //Decrease the minimum height of the content
                const multipliedExpansionIncrementCount = this.expansionIncrementCount * expansionIncrementCountMultiplier;
                const decreaseByValue = multipliedExpansionIncrementCount < this.sectionContentMaxHeight ? multipliedExpansionIncrementCount : this.sectionContentMaxHeight;
                //Math.max is used to eliminate any negative values
                this.decreaseCollapsableContentMaxHeight(Math.max(0, decreaseByValue));

                //Increase the height decrement multiplier
                expansionIncrementCountMultiplier += this.expansionMultiplierIncrement;
            }, this.expansionIncrementPace);
        }
        else {
            this.isSectionResizing = false;
            this.collapsableContentRef.style.maxHeight = '0';
        }
    }

    /**
     * Get the content section's height
     */
    getSectionContentHeight = (): number => this.collapsableContentRef?.offsetHeight ?? 0;

    /**
     * Stop resizing the content section (clear any ongoing setInterval(s))
     */
    stopContentResizing = () => {
        this.isSectionResizing = false;
        this.clearExpansionInterval();
    }

    clearExpansionInterval() {
        if (this.expansionIntervalId) {
            clearInterval(this.expansionIntervalId);
            this.expansionIntervalId = null;
        }
    }
}
